/**
 * IndexCapacityHeatmap.tsx
 * Item 5.3 — daily utilization heatmap for the shared virtual Index machine,
 * mounted below the Phase-2 ("Index tests") allocation table.
 *
 * Horizon = union of this source's allocation windows, padded a few working
 * days each side (min ~3 weeks visible) — see `computeHeatmapHorizon`.
 * Data = `GET /api/schedule/index-capacity` (Item 3.2), refetched whenever the
 * horizon, scenario, this source's allocation content (window/count/status —
 * `allocationsFingerprint`), or `onEnterRefreshKey` changes.
 *
 * Over-capacity days (I9) surface as a warning chip above the grid — WARN
 * ONLY, this never blocks planning (mirrors the rest of the app's >100% rule).
 *
 * Item 5.4 — Compare-to-Baseline ghost (I16): when `compareToBaseline` is
 * true, a second fetch retrieves the BASELINE perspective (`getIndexCapacity`
 * with no `scenarioKey`, mirroring Phase1MachineGrid's baseline booking
 * fetch) over the SAME horizon, and each day cell renders that perspective's
 * firmPct as a dashed ghost badge (`indexBaselineDiff.buildDayBaselineGhostMap`,
 * rendered by `IndexCapacityHeatmapCell`) — same visual language as
 * Phase1MachineGrid's ghost bars.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Box, Chip, CircularProgress, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

import { useScheduleHolidays } from '../../../../../hooks/useScheduleHolidays';
import {
  getIndexCapacity,
  type IndexAllocation,
  type IndexCapacityTypeInfo,
  type IndexDayCapacity,
} from '../../../../../services/scheduleIndex';
import { buildIndexTypeRows, computeHeatmapHorizon, type OwnAllocationInput } from './indexHeatmapModel';
import { buildTypeBaselineGhostMap } from './indexBaselineDiff';
import { IndexCapacityHeatmapGrid } from './IndexCapacityHeatmapGrid';

export interface IndexCapacityHeatmapProps {
  /** Own-scenario perspective for the firm/soft split (I13) — omitted = BASELINE. */
  scenarioKey: string | null;
  /** This source's own pooled allocations (drives the horizon + the own-tentative split). */
  allocations: IndexAllocation[];
  /** 6D.2-style on-enter refresh key — bumps to force a refetch. */
  onEnterRefreshKey?: number;
  /**
   * Post-commit refresh serial (2026-07-28 stale-heatmap fix): the panel bumps
   * this AFTER a window PATCH resolves on the server. The optimistic row update
   * already re-fires the fetch via `fingerprint`, but that GET races the PATCH
   * and can read pre-commit data — and once the PATCH lands, the merged row has
   * the same dates as the optimistic one, so the fingerprint never changes
   * again and the stale numbers stick until a manual reload. This serial forces
   * the one guaranteed post-commit fetch (the effect's `cancelled` cleanup
   * discards whichever racy earlier response is still in flight).
   */
  dataRefreshKey?: number;
  /** Item 5.4 — when true, overlays the BASELINE-perspective ghost per day cell. */
  compareToBaseline?: boolean;
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

/** Extract the backend's error message from an axios-style error (mirrors IndexTestsPanel). */
function errorMessage(err: unknown, fallback: string): string {
  const apiMsg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
  if (apiMsg) return apiMsg;
  return err instanceof Error ? err.message : fallback;
}

/**
 * Cheap content fingerprint so a window/count/status edit re-triggers the
 * capacity fetch without a deep-equality check on every render.
 */
function allocationsFingerprint(allocations: IndexAllocation[]): string {
  return allocations
    .map((a) => `${a.IndexAllocationKey}:${a.StartDate}:${a.EndDate}:${a.TestCount}:${a.Status}`)
    .sort()
    .join('|');
}

function toOwnAllocationInput(a: IndexAllocation): OwnAllocationInput {
  return {
    TaskTypeKey: a.TaskTypeKey,
    StartDate: a.StartDate,
    EndDate: a.EndDate,
    TestCount: a.TestCount,
    IndexTestsPerDay: a.IndexTestsPerDay ?? null,
    Status: a.Status,
  };
}

export const IndexCapacityHeatmap: React.FC<IndexCapacityHeatmapProps> = ({
  scenarioKey,
  allocations,
  onEnterRefreshKey = 0,
  dataRefreshKey = 0,
  compareToBaseline = false,
}) => {
  const theme = useTheme();

  const fingerprint = allocationsFingerprint(allocations);

  const horizon = useMemo(
    () =>
      computeHeatmapHorizon(
        allocations.map((a) => ({ StartDate: a.StartDate, EndDate: a.EndDate })),
        todayIso(),
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [fingerprint],
  );

  const { holidays: holidayRows } = useScheduleHolidays(horizon.start, horizon.end);
  const holidayDates = useMemo(() => holidayRows.map((h) => h.date.slice(0, 10)), [holidayRows]);

  const [days, setDays] = useState<IndexDayCapacity[]>([]);
  const [types, setTypes] = useState<IndexCapacityTypeInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (allocations.length === 0) {
      setDays([]);
      setTypes([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    getIndexCapacity(horizon.start, horizon.end, scenarioKey ?? undefined)
      .then((res) => {
        if (!cancelled) {
          setDays(res.days);
          setTypes(res.types ?? []);
        }
      })
      .catch((err) => {
        if (!cancelled) setError(errorMessage(err, 'Failed to load the Index machine utilization.'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [horizon.start, horizon.end, scenarioKey, fingerprint, onEnterRefreshKey, dataRefreshKey]);

  const ownAllocations = useMemo(() => allocations.map(toOwnAllocationInput), [allocations]);
  // P3 (2026-07-29): one row per index test type, each against its own capacity.
  const typeRows = useMemo(
    () => buildIndexTypeRows(types, days, ownAllocations, holidayDates),
    [types, days, ownAllocations, holidayDates],
  );

  // ── Item 5.4 — Compare-to-Baseline day-cell ghost ──────────────────────────
  // Best-effort second fetch over the SAME horizon, omitting scenarioKey (the
  // BASELINE perspective) — mirrors Phase1MachineGrid's ghost-bar fetch.
  const [baselineDays, setBaselineDays] = useState<IndexDayCapacity[]>([]);

  useEffect(() => {
    if (!compareToBaseline || allocations.length === 0) {
      setBaselineDays([]);
      return;
    }
    let cancelled = false;
    getIndexCapacity(horizon.start, horizon.end)
      .then((res) => {
        if (!cancelled) setBaselineDays(res.days);
      })
      .catch(() => {
        if (!cancelled) setBaselineDays([]);
      });
    return () => {
      cancelled = true;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [compareToBaseline, horizon.start, horizon.end, fingerprint, onEnterRefreshKey, dataRefreshKey]);

  const baselineGhosts = useMemo(
    () => (compareToBaseline ? buildTypeBaselineGhostMap(typeRows, baselineDays) : undefined),
    [compareToBaseline, typeRows, baselineDays],
  );

  // Days where ANY type is over ITS OWN capacity (P3 — never the cross-type sum).
  const overCapacityCount = (typeRows[0]?.cells ?? []).filter((_, i) =>
    typeRows.some((row) => row.cells[i]?.isOverCapacity),
  ).length;

  // I20-style friendly no-op — the tab is normally unreachable with zero
  // allocations; stay quiet rather than fetching/rendering an empty grid.
  if (allocations.length === 0) return null;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, mt: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Typography variant="subtitle1" fontWeight={700} sx={{ fontSize: '0.9rem' }}>
          Daily Utilization — Index Tests (each type has its own capacity)
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {horizon.start} → {horizon.end}
          {loading && <CircularProgress size={10} sx={{ ml: 1, verticalAlign: 'middle' }} />}
        </Typography>

        <Box sx={{ flex: 1 }} />

        {overCapacityCount > 0 && (
          <Chip
            icon={<WarningAmberIcon sx={{ fontSize: '0.85rem' }} />}
            label={`${overCapacityCount} day${overCapacityCount === 1 ? '' : 's'} over capacity`}
            size="small"
            sx={{
              fontWeight: 700,
              fontSize: '0.7rem',
              backgroundColor: alpha(theme.schedule.heatmapHigh, 0.14),
              color: theme.schedule.heatmapHigh,
            }}
          />
        )}
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {typeRows.length > 0 && (
        <IndexCapacityHeatmapGrid rows={typeRows} baselineGhosts={baselineGhosts} />
      )}

      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem' }}>
        One row per index test type — each % is that type's share of ITS OWN tests/day capacity,
        so two types can each run at 100% on the same day. Firm = committed (any scenario) + this
        plan's own tentative rows. Dashed "+N%" = other drafts' tentative — advisory only, never
        blocks. Over 100% (of a type's own capacity) warns, never blocks (I9).
        {compareToBaseline && ' Dashed "base N%" badges (Compare to Baseline) show the committed schedule without this draft.'}
      </Typography>
    </Box>
  );
};
